import xxhash from 'xxhash-wasm';

import { setClassName } from '@change-corgi/core/error';

import { base48encode } from './base48';

export class WasmUnavailableError extends Error {
	constructor() {
		super('WebAssembly is not available');
	}
}
setClassName(WasmUnavailableError, 'WasmUnavailableError');

const DEFAULT_HASH_LENGTH = 10;
const MIN_HASH_LENGTH = 7;
const MAX_HASH_LENGTH = 12;

// Some browsers (e.g. iOS Safari in Lockdown Mode or in-app webviews) do not
// expose WebAssembly. Guard the call so the module can still be imported without
// throwing a ReferenceError, and callers can handle the null/rejection gracefully.
const hasherReady: ReturnType<typeof xxhash> =
	typeof WebAssembly !== 'undefined' ? xxhash() : Promise.reject(new WasmUnavailableError());

// Cached resolved hasher — set once when the WASM module loads.
let syncHasher: Awaited<typeof hasherReady> | null = null;
void hasherReady
	.then((h) => {
		syncHasher = h;
		return h;
	})
	.catch((err: unknown) => {
		if (err instanceof WasmUnavailableError) {
			// Expected: environment has no WebAssembly (e.g. iOS Lockdown Mode).
			// syncHasher stays null; generateShareId will reject for callers to handle.
			return;
		}
		// Unexpected WASM init failure (CSP block, OOM, corrupted binary, etc.) — re-throw so it
		// surfaces as an unhandled rejection and reaches error monitoring.
		throw err;
	});

// Mirror of the lambda's `calculateHash` (longlinks_shortener/api/store.js).
// Algorithm: xxh64(utf8(longUrl), seed=0) → uint64 BigInt → base48 (low N digits).
export async function generateShareId(longUrl: string, hashLength: number = DEFAULT_HASH_LENGTH): Promise<string> {
	if (!Number.isFinite(hashLength) || hashLength < MIN_HASH_LENGTH || hashLength > MAX_HASH_LENGTH) {
		throw new Error(`'hashLength' must be in the range [${MIN_HASH_LENGTH}, ${MAX_HASH_LENGTH}]`);
	}
	const hasher = await hasherReady;
	// Same i64/BigInt interop guard as generateShareIdSync (see comment there). Unlike the sync
	// path, we re-throw as WasmUnavailableError so callers can handle it; other errors propagate.
	try {
		return base48encode(hasher.h64(longUrl), hashLength);
	} catch (err) {
		if (err instanceof TypeError && err.message.includes('illegal type')) {
			throw new WasmUnavailableError();
		}
		throw err;
	}
}

// Synchronous variant — returns null if the WASM hasher isn't loaded yet.
// Safe to call during render; once the module is initialised the value is stable.
export function generateShareIdSync(longUrl: string, hashLength: number = DEFAULT_HASH_LENGTH): string | null {
	if (!syncHasher) return null;
	if (!Number.isFinite(hashLength) || hashLength < MIN_HASH_LENGTH || hashLength > MAX_HASH_LENGTH) {
		throw new Error(`'hashLength' must be in the range [${MIN_HASH_LENGTH}, ${MAX_HASH_LENGTH}]`);
	}
	// Some Android WebViews initialise the WASM module successfully but fail at
	// runtime on i64 operations ("wasm function signature contains illegal type")
	// because they don't implement WASM-BigInt interop. Treat that as equivalent
	// to the hasher being unavailable and clear syncHasher so we don't retry.
	try {
		return base48encode(syncHasher.h64(longUrl), hashLength);
	} catch (err) {
		if (err instanceof TypeError && err.message.includes('illegal type')) {
			syncHasher = null;
			return null;
		}
		throw err;
	}
}
